/**
 * React Query hooks for Jira integration data fetching and mutations.
 * Follows the pattern from useLogfireTokens.ts with onSettled for invalidation.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getJiraIntegration,
  createJiraIntegration,
  updateJiraIntegration,
  deleteJiraIntegration,
  testJiraConnection,
} from "@/services/jiraIntegrationService";
import { useApiReady } from "@/hooks/useApi";
import { ApiError } from "@/services/api";
import type {
  JiraIntegrationCreate,
  JiraIntegrationUpdate,
  JiraConnectionTestRequest,
} from "@/types/jiraIntegration";

/**
 * Hook to fetch the current organization's Jira integration.
 * Returns null (not an error) when no integration is configured (404).
 */
export function useJiraIntegration() {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["jira-integration"],
    queryFn: async () => {
      try {
        return await getJiraIntegration();
      } catch (error) {
        // 404 means not configured — return null instead of throwing
        if (error instanceof ApiError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 30_000,
    enabled: isApiReady,
  });
}

/**
 * Hook to create a new Jira integration.
 * Invalidates the integration query on settled.
 */
export function useCreateJiraIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: JiraIntegrationCreate) => createJiraIntegration(data),
    onSettled: (data, error) => {
      if (error) {
        toast.error("Failed to create Jira integration", {
          description: error instanceof Error ? error.message : undefined,
        });
      } else if (data) {
        toast.success("Jira integration created", {
          description: `Connected to ${data.jira_base_url}`,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["jira-integration"],
        refetchType: "all",
      });
    },
  });
}

/**
 * Hook to update the Jira integration.
 * Invalidates the integration query on settled.
 */
export function useUpdateJiraIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: JiraIntegrationUpdate) => updateJiraIntegration(data),
    onSettled: (data, error) => {
      if (error) {
        toast.error("Failed to update Jira integration", {
          description: error instanceof Error ? error.message : undefined,
        });
      } else if (data) {
        toast.success("Jira integration updated");
      }
      queryClient.invalidateQueries({
        queryKey: ["jira-integration"],
        refetchType: "all",
      });
    },
  });
}

/**
 * Hook to delete the Jira integration.
 * Invalidates the integration query on settled.
 */
export function useDeleteJiraIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteJiraIntegration(),
    onSettled: (_data, error) => {
      if (error) {
        toast.error("Failed to delete Jira integration", {
          description: error instanceof Error ? error.message : undefined,
        });
      } else {
        toast.success("Jira integration deleted");
      }
      queryClient.invalidateQueries({
        queryKey: ["jira-integration"],
        refetchType: "all",
      });
    },
  });
}

/**
 * Hook to test the Jira connection.
 * Accepts optional inline credentials for pre-save testing.
 * No cache invalidation needed — this is a one-off test.
 */
export function useTestJiraConnection() {
  return useMutation({
    mutationFn: ({
      projectKey,
      credentials,
    }: {
      projectKey?: string;
      credentials?: JiraConnectionTestRequest;
    }) => testJiraConnection(projectKey, credentials),
  });
}
